import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Geolocation } from '@capacitor/geolocation';
import { CapacitorHttp } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class LocationSharingService implements OnDestroy {

  private watchId: string | null = null;
  private readonly _sharing$ = new BehaviorSubject<boolean>(false);

  constructor(
    private globalService: GlobalService,
    private userService: UserService
  ) {}

  get sharing(): boolean {
    return this._sharing$.value;
  }

  get sharing$(): Observable<boolean> {
    return this._sharing$.asObservable();
  }

  // startWatch=false: update isSharing$ state only, without starting a watchPosition.
  // Use this when the native plugin already handles location posting (watch-initiated flows)
  // so Capacitor Geolocation's CLLocationManager doesn't keep running after a native stop.
  async start(startWatch = true): Promise<void> {
    if (this._sharing$.value) return;

    this._sharing$.next(true);
    if (!startWatch) return;

    this.watchId = await Geolocation.watchPosition(
      { enableHighAccuracy: true, timeout: 10000 },
      (position, err) => {
        if (err || !position) return;
        this.postLocation(
          position.coords.latitude,
          position.coords.longitude,
          position.coords.accuracy
        ).catch(() => {});
      }
    );
  }

  async stop(): Promise<void> {
    if (!this._sharing$.value) return;
    // Flip state synchronously before any await so concurrent calls hit the guard above.
    this._sharing$.next(false);
    const id = this.watchId;
    this.watchId = null;
    if (id !== null) {
      await Geolocation.clearWatch({ id }).catch(() => {});
    }
  }

  private async postLocation(lat: number, lng: number, accuracy: number): Promise<void> {
    const jwt = this.globalService.jwttoken;
    const userId = this.userService.getUserId();
    if (!jwt || !userId) return;

    await CapacitorHttp.request({
      method: 'POST',
      url: `${environment.apiURL}trainingGroupLiveLocation`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `bearer ${jwt}`,
        'User-Id': userId,
      },
      data: `lat=${lat}&lng=${lng}&accuracy=${accuracy}`,
    });
  }

  ngOnDestroy(): void {
    this.stop().catch(() => {});
  }
}
